import { Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { CustomTopic, GameEngineService, GameService, InternationalizationService, SessionService,
  TransitionService, User
 } from '@app/core/services';
import { JeopardyGame, JeopardyRound, JeopardyUser } from '@app/shared/graphql/types';
import { Observable, Subscription, timer } from 'rxjs';
import { tap } from 'rxjs/operators';

@Component({
    selector: 'jeopardy-waiting-lobby',
    templateUrl: './waiting-lobby.component.html',
    styleUrls: ['./waiting-lobby.component.scss'],
    imports: [RouterLink]
})
export class WaitingLobbyComponent implements OnInit {
  public room: JeopardyGame;
  public room$: Observable<JeopardyGame>;
  public players: JeopardyUser[];
  public counter: string;
  public gameStarted: boolean;
  private currentUser: User;
  public topics: CustomTopic[];
  private roundStartedSub: Subscription;
  private subscriptions: Subscription;
  public gameInactive: boolean;
  public users$: Observable<JeopardyUser[]>;
  public gameLoaded: boolean;
  private joinRoomInt: number;

  private gameService = inject(GameService);
  private sessionService = inject(SessionService);
  private transitionService = inject(TransitionService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  public i18n = inject(InternationalizationService);
  public gameEngineService = inject(GameEngineService);

  ngOnInit(): void {
    this.joinRoomInt = null;
    this.gameLoaded = false;
    this.gameInactive = false;
    this.subscriptions = new Subscription();
    this.currentUser = this.sessionService.getUser();

    const gameId = +this.route.snapshot.paramMap.get('id');

    const userConnectedSub = this.gameService.getUserConnectedSubscription(gameId)
      .pipe(
        tap(() => {
          timer(500).subscribe(() => {
            const gamePlayersSub$ = this.gameEngineService.loadPlayers().subscribe(() => {
              this.players = this.gameEngineService.players.filter(player => player.connected);
              
              if(this.players.length === this.gameEngineService.players.length) {
                this.startGame();
              }
            });
    
            this.subscriptions.add(gamePlayersSub$);
          });
        })
      )
      .subscribe();

    this.subscriptions.add(userConnectedSub);

    if(this.gameEngineService.gameId) {
      this.initGame();
    } else {
      const initGameSub$ = this.gameEngineService.init(gameId).subscribe(_ => {
        this.initGame();
      });
      this.subscriptions.add(initGameSub$);
    }

    const gameInactivedSub = this.gameService.getGameInactivatedSubscription(gameId).subscribe(_ => {
      this.gameInactive = true;
      this.gameEngineService.clearGame();
      this.gameService.finishGame();
      this.sessionService.clearSession();
    });
    this.subscriptions.add(gameInactivedSub);
  }

  private initGame() {
    this.gameLoaded = true;
    this.topics = this.gameEngineService.topics;
    this.gameInactive = !this.gameEngineService.active;
    this.players = this.gameEngineService.players.filter(player => player.connected);

    if(this.players.length === this.gameEngineService.players.length) {
      this.startGame();
    } else {
      this.startCounter();
    }
  }

  private startGame() {
    this.stopCounter();

    this.gameStarted = true;

    this.transitionService.showGameLoader('Starting the game...');

    setTimeout(() => {
      const gameRoundSub$ = this.gameEngineService.getCurrentRound().subscribe(() => {
        const round = this.gameEngineService.currentRound;
  
        const isRoundActiveUser = this.checkRoundActiveUser(round);
  
        setTimeout(() => {
          this.router.navigate(['select-topic']);
  
          if(isRoundActiveUser) {
            setTimeout(() => {
              this.transitionService.hideGameLoader();
            }, 500);
          } else {
            this.transitionService.gameLoaderMessage$.next(`Waiting for ${round.activeUser.name} to select topic`);
          }
        }, 500);
      });
      this.subscriptions.add( gameRoundSub$);
    }, 1000);
  }

  private startCounter() {
    const joinRoomSetting = this.gameEngineService.gameEngineDetails.settingTimerJoinRoom * 60 * 1000;

    const d = new Date(this.gameEngineService.createdOn);
    let createdOn = d.getTime();
    let diff = new Date().getTime() - createdOn;

    const ms = d.getMilliseconds();
    const timeoutUntilNext = 1000 - ms;

    if(diff >= joinRoomSetting - 1000) {
      this.counter = '00:00';
    } else {
      this.counter = this.millisToMinutesAndSeconds(joinRoomSetting - diff);

      setTimeout(() => {
        this.counter = this.millisToMinutesAndSeconds(joinRoomSetting - diff);

        this.joinRoomInt = window.setInterval(() => {
          diff = (new Date().getTime() - createdOn);

          if(diff >= joinRoomSetting - 1000) {
            window.clearInterval(this.joinRoomInt);
            this.gameInactive = true;
          }
          this.counter = this.millisToMinutesAndSeconds(joinRoomSetting - diff);
        }, 1000);
      }, timeoutUntilNext);
    }
  }

  private stopCounter(){
    window.clearInterval(this.joinRoomInt);
  }

  private checkRoundActiveUser(round: JeopardyRound): boolean {
    const deviceDetails = this.gameService.getDeviceDetails();

    const sameLanguage = round.activeUser.language === this.currentUser.location.language.isoCode2Letters;
    const sameLocation = round.activeUser.location === this.currentUser.location.country.countryId;
    const sameName = round.activeUser.name === this.currentUser.username;
    const sameDeviceType = round.activeUser.deviceDetected === deviceDetails[0];
    const sameDeviceId = round.activeUser.deviceIdentifier === deviceDetails[1];

    return sameLanguage && sameLocation && sameName && sameDeviceType && sameDeviceId;
  }

  public millisToMinutesAndSeconds(millis: number) {
    var minutes = Math.floor(millis / 60000);
    var seconds = ((millis % 60000) / 1000).toFixed(0);

    return (
      +seconds == 60 ?
      (minutes+1) + ":00" :
      minutes + ":" + (+seconds < 10 ? "0" : "") + seconds
    );
  }

  ngOnDestroy() {
    if(this.roundStartedSub) {
      this.roundStartedSub.unsubscribe();
    }

    this.subscriptions.unsubscribe();
  }
}